import type { SearchContext } from '@shared/types/types.ts';
import type { OnEx } from '@tilelist/domain/experiments/OnEx.ts';

export type BrandTileBackgroundToken =
    | 'color/background/strong/warm-red'
    | 'color/background/strong/purple'
    | 'color/background/strong/blue'
    | 'color/beige/140';

export type BrandTileCampaign = {
    brand: string;
    advertiserLegalName: string;
    funderLegalName: string;
    backgroundToken: BrandTileBackgroundToken;
    image: {
        src: string;
        alt: string;
        objectPosition?: string;
        transform?: string;
    };
    text: string;
    linkUrl: string;
};

type ApprovedAssortment = 'Bekleidung' | 'Schuhe' | 'Haushaltsgeräte' | 'Unterhaltungselektronik';
type ApprovedTargetGroup = 'Herren' | 'Damen' | 'all';
type CampaignKey = `${ApprovedAssortment}::${ApprovedTargetGroup}`;

function isCampaignKey(key: string): key is CampaignKey {
    return key in BRAND_TILE_CAMPAIGNS;
}

const BRAND_TILE_CAMPAIGNS: Partial<Record<CampaignKey, BrandTileCampaign>> = {
    'Bekleidung::Herren': {
        brand: 'SNOCKS',
        advertiserLegalName: 'Otto GmbH & Co. KGaA',
        funderLegalName: 'Snocks GmbH',
        backgroundToken: 'color/background/strong/warm-red',
        image: {
            src: '/static-proxy/dundee/brand-tile/snocks.webp',
            alt: 'SNOCKS',
            objectPosition: '64%',
        },
        text: 'Premium-Produkte.',
        linkUrl:
            '/p/snocks-boxershorts-enge-unterhosen-herren-maenner-6-st-aus-bio-baumwolle-ohne-kratzenden-zettel-S0Y240VF/?variationId=S0M0A0ZVA80E',
    },
    'Bekleidung::Damen': {
        brand: 'SASSYCLASSY',
        advertiserLegalName: 'Otto GmbH & Co. KGaA',
        funderLegalName: 'RODEBO-Lifestyle GmbH',
        backgroundToken: 'color/background/strong/purple',
        image: {
            src: '/static-proxy/dundee/brand-tile/sassyclassy.webp',
            alt: 'SASSYCLASSY',
            objectPosition: '68% center',
        },
        text: 'Deine Saison. Verspielt. Selbstbewusst.',
        linkUrl: '/?selektion=(und.(ist.marke.sassyclassy).(ist.verkaeufer.sassyclassy).(~.(v.1)))',
    },
    'Schuhe::Herren': {
        brand: 'adidas',
        advertiserLegalName: 'Otto GmbH & Co. KGaA',
        funderLegalName: 'adidas AG',
        backgroundToken: 'color/background/strong/warm-red',
        image: {
            src: '/static-proxy/dundee/brand-tile/adidas.webp',
            alt: 'adidas',
            objectPosition: '49% center',
        },
        text: 'Deine Streetstyle Must-Haves',
        linkUrl:
            '/?selektion=(und.(ist.marke.adidas-originals.adidas-performance.adidas-sportswear).(ist.verkaeufer.adidas-ag).(ist.zielgruppe.herren).(oder.(sind.kategorien.sneaker).(sind.kategorien.sportschuhe)).(suchbegriff.adidas).(~.(v.1)))',
    },
    'Schuhe::Damen': {
        brand: 'Ital-Design',
        advertiserLegalName: 'Otto GmbH & Co. KGaA',
        funderLegalName: 'D.I.Y. Import Export GmbH',
        backgroundToken: 'color/beige/140',
        image: {
            src: '/static-proxy/dundee/brand-tile/italdesign.webp',
            alt: 'Ital-Design',
            objectPosition: '52% center',
        },
        text: 'Stilvolle Allrounder für jede Gelegenheit.',
        linkUrl:
            '/p/ital-design-elegante-loafers-fuer-damen-mit-komfort-und-stilvollem-design-slipper-91117117-blockabsatz-mokassins-in-hellbraun-S0YG30XF/?variationId=S0YG30XFRX6F',
    },
    'Haushaltsgeräte::all': {
        brand: 'Moctor',
        advertiserLegalName: 'Otto GmbH & Co. KGaA',
        funderLegalName: 'Rotcom GmbH & Co. KG',
        backgroundToken: 'color/background/strong/blue',
        image: {
            src: '/static-proxy/dundee/brand-tile/moctor.webp',
            alt: 'Moctor',
            objectPosition: '45% center',
        },
        text: 'Innovative Technologie für dein Zuhause',
        linkUrl:
            '/p/moctor-waermepumpentrockner-dh9009a3w-startzeitauswahl-zwei-wege-schleudern-moctor-app-funktion-S0NCS0SD/',
    },
    'Unterhaltungselektronik::all': {
        brand: 'medion',
        advertiserLegalName: 'Otto GmbH & Co. KGaA',
        funderLegalName: 'MEDION GmbH',
        backgroundToken: 'color/background/strong/blue',
        image: {
            src: '/static-proxy/dundee/brand-tile/medion.webp',
            alt: 'medion',
            objectPosition: '50% 150%',
            transform: 'scale(1.3)',
        },
        text: 'Kino-Feeling für Zuhause!',
        linkUrl: '/technik/fernseher/?marke=medion&verkaeufer=medionshop-deutschland',
    },
    'Schuhe::all': {
        brand: 'adidas',
        advertiserLegalName: 'Otto GmbH & Co. KGaA',
        funderLegalName: 'adidas AG',
        backgroundToken: 'color/background/strong/warm-red',
        image: {
            src: '/static-proxy/dundee/brand-tile/adidas.webp',
            alt: 'adidas',
            objectPosition: '49% center',
        },
        text: 'Deine Streetstyle Must-Haves',
        linkUrl:
            '/?selektion=(und.(ist.marke.adidas-originals.adidas-performance.adidas-sportswear).(ist.verkaeufer.adidas-ag).(ist.zielgruppe.herren).(oder.(sind.kategorien.sneaker).(sind.kategorien.sportschuhe)).(suchbegriff.adidas).(~.(v.1)))',
    },
};

export function getBrandTileCampaign(searchContext?: SearchContext): BrandTileCampaign | undefined {
    const assortment = searchContext?.assortment ?? searchContext?.topNImplicit?.assortmentNavigation?.[0];
    const targetGroup = searchContext?.explicit?.targetGroup?.[0] ?? searchContext?.implicit?.targetGroup?.[0];

    if (!assortment) {
        return undefined;
    }

    if (targetGroup) {
        const key = `${assortment}::${targetGroup}`;

        if (isCampaignKey(key)) {
            return BRAND_TILE_CAMPAIGNS[key];
        }
    }

    const fallbackKey = `${assortment}::all`;

    if (isCampaignKey(fallbackKey)) {
        return BRAND_TILE_CAMPAIGNS[fallbackKey];
    }

    return undefined;
}

export function isBrandTileExperimentEligible(searchContext?: SearchContext): boolean {
    return getBrandTileCampaign(searchContext) !== undefined;
}

export function isBrandTileTreatment(searchContext: SearchContext | undefined, onex: OnEx | undefined): boolean {
    return isBrandTileExperimentEligible(searchContext) && (onex?.isExperimentGroup('BRAND_TILE', 'E3804B') ?? false);
}
